import type { Ref } from 'vue';

import type { IContextMutationResult } from '@modules/core/services/query/types';
import type { IPaymentSystemQueryType, IPaymentSystemWithdrawalPaylod } from '@modules/payment-systems/types';
import type { Permission } from '@modules/permissions/utils';
import { useRestClient } from '@modules/core/services/api';
import { useContextMutation } from '@modules/core/services/query';
import { useCurrentNamespace } from '@modules/permissions/composables';
import { useToastStore } from '@modules/toast/store';

import {
  ADMIN_PAYMENT_SYSTEM_WITHDRAWAL_CASHOUT_URL,
  ADMIN_PAYMENT_SYSTEM_WITHDRAWAL_URL,
  AFFILIATE_PAYMENT_SYSTEM_WITHDRAWAL_CASHOUT_URL,
  AFFILIATE_PAYMENT_SYSTEM_WITHDRAWAL_URL,
  PAYMENT_SYSTEM_ALLOWED_QUERY_KEY,
  PAYMENT_SYSTEM_WITHDRAWAL_MUTATION_KEY,
} from '../constants';

export default function usePaymentSystemsWithdrawalMutation(
  queryType: Ref<IPaymentSystemQueryType>,
  permission: Ref<Permission>,
): IContextMutationResult<
    void,
    Error,
    IPaymentSystemWithdrawalPaylod
  > {
  const { displayPresetToast } = useToastStore();
  const { fetchPost } = useRestClient();
  const { isAdminNamespace } = useCurrentNamespace();

  return useContextMutation<void, Error, IPaymentSystemWithdrawalPaylod>({
    mutationKey: [PAYMENT_SYSTEM_WITHDRAWAL_MUTATION_KEY],
    mutationFn: ({ affiliate_id, payment_system_name, ...payment_system }) => {
      let url = '';

      if (isAdminNamespace.value) {
        if (queryType.value === 'default') {
          url = ADMIN_PAYMENT_SYSTEM_WITHDRAWAL_URL;
        } else if (queryType.value === 'cashout') {
          url = ADMIN_PAYMENT_SYSTEM_WITHDRAWAL_CASHOUT_URL;
        }
      } else if (queryType.value === 'default') {
        url = AFFILIATE_PAYMENT_SYSTEM_WITHDRAWAL_URL;
      } else if (queryType.value === 'cashout') {
        url = AFFILIATE_PAYMENT_SYSTEM_WITHDRAWAL_CASHOUT_URL;
      }

      return fetchPost<void>(
        url,
        {
          payment_system: {
            ...payment_system,
            payment_system_name: queryType.value === 'cashout' ? payment_system_name : undefined,
            name: queryType.value === 'default' ? payment_system_name : undefined,
          },
          affiliate_id,
        },
        {
          permission: permission.value,
          urlParams: { id: affiliate_id },
        },
      );
    },
    onSuccess({ queryClient }) {
      displayPresetToast('success');
      void queryClient.invalidateQueries({ queryKey: [PAYMENT_SYSTEM_ALLOWED_QUERY_KEY] });
    },
  });
}
